import type { CSSProperties } from 'react'
import type { AuthUser } from '@yoreflix/core/api/types'
import { BusyButton } from './components/BusyButton'
import { ProjectsScreen } from './screens/ProjectsScreen'

/**
 * The signed-in shell — U0 is the scaffold only: chrome + sign-out + an empty state. The real
 * screens land later (upload-portal-plan.md phasing): U3 fills the body with Projects, the
 * drag-drop file surface, the transfer queue and live progress.
 *
 * Chrome deliberately mirrors admin/dashboard (same tokens, same `.admin-btn` hover contract) so
 * the three portals read as one product. Kept intentionally thin — no side menu or nav yet,
 * because there is exactly one place to be until U3 introduces more than one.
 */

const page: CSSProperties = {
  minHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--background)',
  color: 'var(--foreground)',
}

const topbar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '12px 20px',
  borderBottom: '1px solid var(--border)',
  background: 'var(--card)',
}

const brand: CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 10 }
const wordmark: CSSProperties = { fontSize: 'var(--fs-heading)', fontWeight: 700, letterSpacing: 0.2 }
const tag: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-sm)', fontWeight: 500 }

const whoami: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  color: 'var(--muted-foreground)',
  fontSize: 'var(--fs-sm)',
}

const signOutBtn: CSSProperties = {
  border: '1px solid var(--border)',
  color: 'var(--foreground)',
  borderRadius: 'var(--radius)',
  padding: '7px 12px',
  fontSize: 'var(--fs-sm)',
  cursor: 'pointer',
}

const foot: CSSProperties = {
  padding: '8px 20px',
  borderTop: '1px solid var(--border)',
  color: 'var(--muted-foreground)',
  fontSize: 'var(--fs-xs)',
  fontFamily: 'var(--font-number)',
  textAlign: 'right',
}

export function UploadShell({ user, onSignOut }: { user: AuthUser; onSignOut: () => void }) {
  return (
    <div style={page}>
      <header style={topbar}>
        <div style={brand}>
          <span style={wordmark}>Yoreflix</span>
          <span style={tag}>Upload</span>
        </div>
        <div style={whoami}>
          <span>{user.EmailAddress}</span>
          {/* ⚠ Sign-out is a ~3s round trip. It must acknowledge the press immediately. */}
          <BusyButton className="admin-btn" style={signOutBtn} onClick={onSignOut} busyLabel="Signing out…">
            Sign out
          </BusyButton>
        </div>
      </header>

      <ProjectsScreen />

      <footer style={foot}>{__BUILD_STAMP__}</footer>
    </div>
  )
}
